'use client'
import { logger } from '@/utils/logger'
import { usePathname } from 'next/navigation'
import { pathHasExperiment } from '@/utils/experiments'
import { ReactNode, useEffect, useState, useRef, useCallback } from 'react'

interface CSPostHogProviderProps {
  children: ReactNode
}

/**
 * Fired on window once posthog.init has finished loading. Consumers that
 * need a fully initialized client (e.g. MarketplaceSessionRecording) listen
 * for this, because usePostHog returns the same global instance before and
 * after init and so never re-triggers effects on load.
 */
export const POSTHOG_LOADED_EVENT = 'posthog:loaded'

/**
 * CSPostHogProvider - Highly optimized PostHog provider
 *
 * Performance optimizations:
 * 1. Deferred loading: Loads PostHog only after user interaction (scroll, click, touch, keydown)
 *    OR after 5 seconds timeout (whichever comes first) via handleInteraction callbacks
 * 2. Non-blocking init: Uses requestIdleCallback in loadPostHog for non-blocking initialization
 * 3. Event buffering: PostHog's library handles event buffering internally - events called before
 *    initialization are queued by PostHog itself (not by this component)
 * 4. Bundle optimization: Reduces initial bundle size by ~189KB through dynamic import
 */
export default function CSPostHogProvider({ children }: CSPostHogProviderProps) {
  const [PostHogProvider, setPostHogProvider] = useState<any>(null)
  const [posthogClient, setPosthogClient] = useState<any>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const loadingRef = useRef(false)
  const hasInteractedRef = useRef(false)
  const eagerLoadRef = useRef(false)
  const pathname = usePathname()

  /** Imports and initializes posthog-js once, off the critical path. */
  const loadPostHog = useCallback(() => {
    if (loadingRef.current) return
    loadingRef.current = true

    const doLoad = () => {
      Promise.all([import('posthog-js'), import('posthog-js/react')])
        .then(([posthogModule, posthogReactModule]) => {
          const posthog = posthogModule.default

          // Initialize PostHog with performance-optimized settings
          posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
            api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST as string,
            // ui_host keeps toolbar/dashboard links pointing at PostHog itself
            ui_host: 'https://us.posthog.com',
            // Pinned defaults snapshot; explicit options below take precedence
            defaults: '2026-05-30',
            // The snapshot's 250ms write debounce drops session referrer and utm on full navigations
            persistence_save_debounce_ms: 0,
            person_profiles: 'identified_only' as const,
            capture_pageview: false,
            capture_pageleave: true,
            autocapture: false,
            request_batching: true,
            disable_surveys: true,
            // Session replay is scoped to marketplace routes: kept off here,
            // started/stopped per-route by MarketplaceSessionRecording.
            disable_session_recording: true,
            session_recording: {
              maskAllInputs: true,
            },
            loaded: () => {
              window.dispatchEvent(new Event(POSTHOG_LOADED_EVENT))
            },
          })

          setPosthogClient(posthog)
          setPostHogProvider(() => posthogReactModule.PostHogProvider)
        })
        .catch((err) => {
          logger.error('Failed to load PostHog', { error: err instanceof Error ? err.message : 'Unknown error' })
          loadingRef.current = false
        })
    }

    // Experiment pages skip the idle deferral too: the flag request has to
    // go out as early as possible so the variant beats the anti-flicker
    // timeout in useExperiment (MAR-5415).
    if (eagerLoadRef.current) {
      doLoad()
      return
    }

    // Use requestIdleCallback for non-blocking load
    if ('requestIdleCallback' in window) {
      requestIdleCallback(doLoad, { timeout: 2000 })
    } else {
      setTimeout(doLoad, 100)
    }
  }, [])

  useEffect(() => {
    // Only in production
    if (
      typeof window === 'undefined' ||
      process.env.NODE_ENV !== 'production' ||
      !process.env.NEXT_PUBLIC_POSTHOG_KEY
    ) {
      return
    }

    // Handle user interaction - load PostHog on first interaction
    const handleInteraction = () => {
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true
        setShouldLoad(true)
      }
    }

    // Listen for user interactions
    const events = ['scroll', 'mousedown', 'touchstart', 'keydown']
    events.forEach((event) => {
      window.addEventListener(event, handleInteraction, { passive: true, once: true })
    })

    // Fallback: load after 5 seconds even without interaction
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let idleCallbackId: ReturnType<typeof requestIdleCallback> | null = null

    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(
        () => {
          setShouldLoad(true)
        },
        { timeout: 5000 },
      )
    } else {
      timeoutId = setTimeout(() => {
        setShouldLoad(true)
      }, 5000)
    }

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleInteraction)
      })
      if (timeoutId) clearTimeout(timeoutId)
      // Use explicit null check - requestIdleCallback can return 0 which is a valid ID
      if (idleCallbackId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId)
      }
    }
  }, [])

  // Experiment pages bypass the interaction/idle gate entirely: an A/B
  // variant must be known before the tested element paints, and the
  // lazy-load resolves flags seconds too late for that. Keyed to pathname
  // so SPA navigations into an experiment page (including programmatic
  // redirects with no preceding interaction) also trigger the load.
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      process.env.NODE_ENV !== 'production' ||
      !process.env.NEXT_PUBLIC_POSTHOG_KEY
    ) {
      return
    }
    if (pathname && pathHasExperiment(pathname)) {
      eagerLoadRef.current = true
      setShouldLoad(true)
    }
  }, [pathname])

  // Load PostHog when shouldLoad becomes true
  useEffect(() => {
    if (shouldLoad && !loadingRef.current) {
      loadPostHog()
    }
  }, [shouldLoad, loadPostHog])

  // If PostHog is not loaded yet or not in production, just render children
  if (!PostHogProvider || !posthogClient) {
    return <>{children}</>
  }

  return <PostHogProvider client={posthogClient}>{children}</PostHogProvider>
}
